import { FC, Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Text } from "@radix-ui/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { DateTime } from "luxon";

import { atlasRoutes } from "@/app/router/routes";
import { api } from "@/modules/api";
import { Uuid } from "@/domains/global/identifiers";
import { AccountResetDialog } from "@/pages/accounts/view/account-reset-dialog";
import { SoftDeleteAccountDialog } from "@/pages/accounts/view/soft-delete-account-dialog";

export const AccountsViewPage: FC = () => {
  const { accountId } = useParams<{ accountId: Uuid }>();
  const queryClient = useQueryClient();

  if (!accountId) {
    return <div>Invalid ID</div>;
  }

  const result = useQuery({
    queryKey: ["account-by-id", accountId],
    queryFn: () =>
      api.get("/internal/v1/accounts/{account_id}", {
        params: {
          path: {
            account_id: accountId,
          },
        },
      }),
  });

  const account = result.data?.data;

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return "—";
    }

    const parsed = DateTime.fromISO(value);
    if (!parsed.isValid) {
      return value;
    }

    return parsed.toFormat("MMM dd, yyyy h:mm a");
  };

  const keyDates: { label: string; value?: string | null }[] = [
    { label: "Created At", value: account?.created_at },
    { label: "Provisioned At", value: account?.provisioned_at },
    { label: "Deprovisioned At", value: account?.deprovisioned_at },
    { label: "Deleted At", value: account?.deleted_at ?? account?.soft_deleted_at },
  ];

  return (
    <div>
      <h1>View Account</h1>
      <Section>
        <SectionTitle size="3">Key Dates</SectionTitle>
        <DefinitionList>
          {keyDates.map(({ label, value }) => (
            <Fragment key={label}>
              <DefinitionLabel>{label}</DefinitionLabel>
              <DefinitionValue>{formatDateTime(value)}</DefinitionValue>
            </Fragment>
          ))}
        </DefinitionList>
      </Section>

      <pre>{JSON.stringify(result.data, null, 2)}</pre>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 32 }}>
        <Button asChild variant="outline">
          <Link
            to={atlasRoutes.accountMigrateEmailAddress({ params: { accountId } }).path}
            state={{ referrer: atlasRoutes.accountsView({ params: { accountId } }).path }}
          >
            Migrate Email Address
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to={atlasRoutes.accountImportFromMem1Dot0({ params: { accountId } }).path}>
            Import from Mem 1.0
          </Link>
        </Button>
        <AccountResetDialog
          accountId={accountId}
          primarySpaceAccountId={account?.primary_space_account_id}
          onAfterReset={async () => {
            await result.refetch();
          }}
        />

        <SoftDeleteAccountDialog
          accountId={accountId}
          profileDisplayName={account?.profile_display_name}
          profileEmailAddress={account?.profile_email_address}
          onSoftDeleted={async () => {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["accounts-search"] }),
              result.refetch(),
            ]);
          }}
        />
      </div>
    </div>
  );
};

const Section = styled.section`
  margin-top: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled(Text)`
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
`;

const DefinitionList = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 16px;
  row-gap: 8px;
  margin: 0;
`;

const DefinitionLabel = styled.dt`
  font-weight: 500;
  color: var(--gray-12);
`;

const DefinitionValue = styled.dd`
  margin: 0;
`;
